import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { services } from '../data/services';

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: 'easeOut' }}
      className="service-page-card"
      style={{
        background: '#F9F9F9',
        padding: '2rem',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ color: '#F5C300', marginBottom: '1rem' }}>{service.icon}</div>
      <h2 style={{
        fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
        fontSize: '1.4rem', color: '#1A1A1A', margin: '0 0 0.6rem',
        letterSpacing: '0.03em', lineHeight: 1.1,
      }}>
        {service.title}
      </h2>
      <p style={{
        fontFamily: 'Inter, sans-serif', color: '#555', fontSize: '0.875rem',
        lineHeight: 1.75, margin: '0 0 1.5rem', flexGrow: 1,
      }}>
        {service.shortDesc}
      </p>
      <Link
        to="/contact"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          background: '#F5C300', color: '#1A1A1A',
          fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
          fontSize: '0.875rem', letterSpacing: '0.08em',
          padding: '0.6rem 1.25rem', borderRadius: '9999px', textDecoration: 'none',
          transition: 'background 0.2s, transform 0.2s',
          alignSelf: 'flex-start',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#e6b800'; e.currentTarget.style.transform = 'scale(1.03)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#F5C300'; e.currentTarget.style.transform = 'scale(1)'; }}
      >
        BOOK THIS SERVICE
        <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      </Link>
    </motion.div>
  );
};

const Services = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{ paddingTop: '80px' }}
    >
      {/* ── HERO BANNER ─────────────────────────────────────── */}
      <section style={{ background: '#1A1A1A', padding: 'clamp(3.5rem, 8vw, 6rem) 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span style={{ fontFamily: 'Inter, sans-serif', color: '#F5C300', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              North City Service and Tyre Centre
            </span>
            <h1 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(3rem, 7vw, 5.5rem)', color: '#ffffff', margin: '0.3rem 0 1rem', lineHeight: 1, letterSpacing: '0.02em' }}>
              What We <span style={{ color: '#F5C300' }}>Do</span>
            </h1>
            <div style={{ width: '60px', height: '4px', background: '#F5C300', marginBottom: '1.5rem' }} />
            <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: '580px', margin: 0 }}>
              From WOF inspections and engine servicing to ECU chiptuning and 3D wheel alignment, our qualified technicians cover everything your vehicle needs. MTA assured, honest and reliable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── SERVICE CARD GRID ───────────────────────────────── */}
      <section style={{ padding: 'clamp(3rem, 6vw, 5rem) 1.5rem', background: '#ffffff' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <style>{`
            .services-page-grid { grid-template-columns: 1fr; }
            .service-page-card { border-top: 4px solid transparent; transition: border-top-color 0.3s; }
            .service-page-card:hover { border-top-color: #F5C300; }
            @media(min-width: 768px) { .services-page-grid { grid-template-columns: repeat(3, 1fr); } }
          `}</style>
          <div className="services-page-grid" style={{ display: 'grid', gap: '1.25rem' }}>
            {services.map((service, i) => (
              <ServiceCard key={service.id} service={service} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ──────────────────────────────────────── */}
      <section style={{ background: '#F5C300', padding: 'clamp(3rem, 6vw, 4.5rem) 1.5rem', textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: '#1A1A1A', margin: '0 0 0.75rem' }}>
            Need a Service?
          </h2>
          <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(26,26,26,0.75)', fontSize: '1.05rem', margin: '0 0 2rem' }}>
            Book online or call us. We will get your vehicle sorted.
          </p>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: '#1A1A1A', color: '#ffffff',
              fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
              fontSize: '1.05rem', letterSpacing: '0.08em',
              padding: '0.85rem 2.5rem', borderRadius: '9999px', textDecoration: 'none',
              transition: 'background 0.25s, transform 0.25s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#333'; e.currentTarget.style.transform = 'scale(1.03)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#1A1A1A'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            BOOK NOW
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default Services;
